import { ICalendarEvent } from '../interfaces/CleaningPlan.types';

export const INLINE_OVERLAP_COLUMN_LIMIT = 10;
export const FULLSCREEN_OVERLAP_COLUMN_LIMIT = 20;

interface SlotRange {
  top: number;
  height: number;
}

interface SlotMetricsLike {
  getRange: (start: Date, end: Date) => SlotRange;
}

interface AccessorsLike {
  start: (event: ICalendarEvent) => Date;
  end: (event: ICalendarEvent) => Date;
}

interface DayLayoutStyle {
  top: number | string;
  height: number | string;
  width: number | string;
  xOffset: number | string;
}

interface StyledEvent {
  event: ICalendarEvent;
  style: DayLayoutStyle;
}

interface DayLayoutArgs {
  events: ICalendarEvent[];
  minimumStartDifference: number;
  slotMetrics: SlotMetricsLike;
  accessors: AccessorsLike;
}

interface EventRange {
  event: ICalendarEvent;
  top: number;
  height: number;
}

const rangesOverlap = (a: EventRange, b: EventRange): boolean => {
  const aEnd = a.top + a.height;
  const bEnd = b.top + b.height;

  return a.top < bEnd && b.top < aEnd;
};

const groupOverlappingRanges = (ranges: EventRange[]): EventRange[][] => {
  const groups: EventRange[][] = [];
  const visited = new Set<number>();

  ranges.forEach((range, index) => {
    if (visited.has(index)) return;

    const group = [range];
    visited.add(index);

    let cursor = 0;

    while (cursor < group.length) {
      const member = group[cursor];

      ranges.forEach((other, otherIndex) => {
        if (!visited.has(otherIndex) && rangesOverlap(member, other)) {
          group.push(other);
          visited.add(otherIndex);
        }
      });

      cursor += 1;
    }

    groups.push(group);
  });

  return groups;
};

const layoutColumn = (count: number, index: number): Pick<DayLayoutStyle, 'width' | 'xOffset'> => {
  const size = 100 / count;

  return {
    width: `${size}%`,
    xOffset: `${size * index}%`,
  };
};

const createMoreEventsIndicator = (hiddenEvents: ICalendarEvent[], accessors: AccessorsLike): ICalendarEvent => {
  const startTimes = hiddenEvents.map(event => accessors.start(event).getTime());
  const endTimes = hiddenEvents.map(event => accessors.end(event).getTime());

  return {
    title: `+${hiddenEvents.length} more`,
    start: new Date(Math.min(...startTimes)),
    end: new Date(Math.max(...endTimes)),
    resource: {
      cleaningPlanEvent: hiddenEvents[0].resource.cleaningPlanEvent,
      hiddenEvents,
    },
  };
};

export const isCleaningPlanMoreEvents = (event: ICalendarEvent): boolean =>
  Boolean(event.resource.hiddenEvents?.length);

/**
 * Lays overlapping events side by side up to a column cap. Extra events are
 * collapsed into a trailing "+N more" indicator instead of shrinking forever.
 */
export const createCappedOverlapDayLayoutAlgorithm =
  (maxVisibleColumns: number) =>
  ({ events, slotMetrics, accessors }: DayLayoutArgs): StyledEvent[] => {
    const columnLimit = Math.max(1, maxVisibleColumns);
    const ranges: EventRange[] = events.map(event => {
      const { top, height } = slotMetrics.getRange(accessors.start(event), accessors.end(event));

      return { event, top, height };
    });

    return groupOverlappingRanges(ranges).flatMap(group => {
      const sorted = [...group].sort((a, b) => accessors.start(a.event).getTime() - accessors.start(b.event).getTime());
      const visibleRanges = sorted.length > columnLimit ? sorted.slice(0, columnLimit - 1) : sorted;
      const hiddenRanges = sorted.length > columnLimit ? sorted.slice(columnLimit - 1) : [];
      const columnCount = visibleRanges.length + (hiddenRanges.length > 0 ? 1 : 0);

      const styledVisible = visibleRanges.map((range, index) => ({
        event: range.event,
        style: {
          top: range.top,
          height: `calc(${range.height}% - 2px)`,
          ...layoutColumn(columnCount, index),
        },
      }));

      if (hiddenRanges.length === 0) {
        return styledVisible;
      }

      const moreEvent = createMoreEventsIndicator(
        hiddenRanges.map(range => range.event),
        accessors
      );
      const { top, height } = slotMetrics.getRange(accessors.start(moreEvent), accessors.end(moreEvent));

      return [
        ...styledVisible,
        {
          event: moreEvent,
          style: {
            top,
            height: `calc(${height}% - 2px)`,
            ...layoutColumn(columnCount, visibleRanges.length),
          },
        },
      ];
    });
  };

export const inlineCappedOverlapDayLayoutAlgorithm = createCappedOverlapDayLayoutAlgorithm(INLINE_OVERLAP_COLUMN_LIMIT);

export const fullscreenCappedOverlapDayLayoutAlgorithm = createCappedOverlapDayLayoutAlgorithm(
  FULLSCREEN_OVERLAP_COLUMN_LIMIT
);
